import React, {useState} from 'react';

import cs from 'classnames';

import NoResultsMessage from 'app/components/general/NoResultsMessage';
import TabbedModal from 'app/components/general/TabbedModal';
import {useCurrentUserState} from 'app/current-user/api';
import {DashboardItemsCounter} from 'app/general/dashboards/DashboardItemsCounter';
import {useLanes} from 'app/lanes/api';
import {SIDEBAR_PAGE_SIZE} from 'app/utils/constants';
import {gt, ngt} from 'app/utils/i18n';

import {ButtonPagination} from './ButtonPagination';
import {CreateLaneListItem} from './CreateLaneListItem';
import {ManageLanesItemList} from './ManageLanesItemList';

interface Props {
  show: boolean;
  onClose: () => void;
}

export function ManageLanesModal({show, onClose}: Props): JSX.Element {
  return (
    <TabbedModal
      show={show}
      onCancel={onClose}
      scrollable={true}
      tabs={[
        {
          eventKey: 'manage-lanes',
          name: gt('Manage lanes'),
          icon: 'fas fa-road',
          content: <ManageLanesTab />,
        },
      ]}
    />
  );
}

function ManageLanesTab() {
  const [page, setPage] = useState(1);
  const [isCreating, setCreating] = useState(false);

  const {data, isError} = useLanes({page, page_size: SIDEBAR_PAGE_SIZE});
  const {is_super_manager: canCreate} = useCurrentUserState().data!;

  if (isError) {
    return (
      <NoResultsMessage
        onSidebar
        icon="fa-exclamation-triangle text-warning"
        head={gt('Could not load the lanes')}
      >
        {gt('Please try again later')}
      </NoResultsMessage>
    );
  }

  if (!data) {
    return (
      <div className="font-italic text-muted py-4 text-center mb-3">
        <i className="fa fa-circle-notch fa-spin mr-1" />
        {gt('Loading lanes...')}
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row justify-content-between">
        <DashboardItemsCounter text={ngt('%s lane', '%s lanes', data.count)} />
        <button
          className={cs('btn btn-link btn-sm', {'d-none': !canCreate})}
          onClick={() => setCreating(true)}
        >
          <i className="fas fa-plus-circle mr-1" />
          {gt('Add new Lane')}
        </button>
      </div>

      <CreateLaneListItem
        show={isCreating}
        onCancel={() => setCreating(false)}
      />

      {data.results.length === 0 && (
        <NoResultsMessage
          onSidebar
          icon="fas fa-road"
          head={gt('No lanes created yet.')}
          className="d-flex flex-column align-items-center"
        />
      )}

      {data.results.map((lane, index) => (
        <ManageLanesItemList
          key={lane.hashid}
          lane={lane}
          borderBottom={index === data.results.length - 1}
        />
      ))}

      <div className="mt-2">
        <ButtonPagination
          currentPage={page}
          response={data}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}
